import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type VisibilityState,
} from '@tanstack/react-table';
import { Eye } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { UpcomingPayment } from '~entities/payment/api/upcoming-payments/types';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { Badge } from '~shared/shadcn/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import {
  UPCOMING_PAYMENT_STATUS_BADGE,
  type UpcomingPaymentsTableProps,
} from './upcoming-payments-table.types';
import { formatUpcomingPaymentDueDate } from './upcoming-payments.helpers';

const columnHelper = createColumnHelper<UpcomingPayment>();

function UpcomingPaymentStatusBadge({
  status,
}: {
  status: UpcomingPayment['status'];
}) {
  const config = UPCOMING_PAYMENT_STATUS_BADGE[status];

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export function UpcomingPaymentsTable({
  formatCurrency,
  isUpdating,
  onPaginationChange,
  onSortingChange,
  pagination,
  payments,
  rowCount,
  sorting,
}: UpcomingPaymentsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor('invoiceId', {
        id: 'Invoice ID',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Invoice ID'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4 font-mono text-sm'>
            {info.getValue().slice(-8)}
          </div>
        ),
      }),
      columnHelper.accessor('dueDate', {
        id: 'Due Date',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Due Date'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {formatUpcomingPaymentDueDate(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor('parentName', {
        id: 'Parent',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Name'
            disableHiding
          />
        ),
        cell: (info) => {
          const payment = info.row.original;
          return (
            <div className='px-4'>
              <Link
                to='/family'
                search={{ parentId: payment.parentId }}
                className='capitalize text-blue-600 hover:text-blue-800 hover:underline'
              >
                {info.getValue()}
              </Link>
            </div>
          );
        },
      }),
      columnHelper.accessor('studentName', {
        id: 'Student',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
            disableHiding
          />
        ),
        cell: (info) => {
          const payment = info.row.original;
          return (
            <div className='px-4'>
              <Link
                to='/students/$studentId/details'
                params={{ studentId: payment.studentId }}
                className='capitalize text-blue-600 hover:text-blue-800 hover:underline'
              >
                {info.getValue()}
              </Link>
            </div>
          );
        },
      }),
      columnHelper.accessor('classChargeName', {
        id: 'Class/Charge Name',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Class/Charge Name'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'Status',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            <UpcomingPaymentStatusBadge status={info.getValue()} />
          </div>
        ),
      }),
      columnHelper.accessor('baseAmount', {
        id: 'Base Amount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Base Amount'
            disableHiding
          />
        ),
        cell: (info) => {
          const payment = info.row.original;
          return (
            <div className='px-4'>
              <div className='space-y-1 md:hidden'>
                <p className='font-medium'>{formatCurrency(info.getValue())}</p>
                {payment.lineItems.map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className='flex justify-between gap-2 text-xs text-gray-600'
                  >
                    <span className='min-w-0 break-words'>
                      {item.name} × {item.quantity}
                    </span>
                    <span className='shrink-0'>
                      {formatCurrency(item.total)}
                    </span>
                  </div>
                ))}
              </div>
              <div className='hidden md:block'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex cursor-pointer items-center gap-2 hover:opacity-80'>
                      {formatCurrency(info.getValue())}
                      <Eye className='size-3 opacity-50' />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className='w-80 border border-gray-200 bg-white p-4 shadow-lg'>
                    <div className='space-y-2'>
                      <h4 className='font-medium'>Line Items</h4>
                      <div className='space-y-1'>
                        {payment.lineItems.map((item, index) => (
                          <div
                            key={`${item.name}-${index}`}
                            className='flex justify-between border-b pb-1 text-sm'
                          >
                            <div>
                              <div className='font-medium'>{item.name}</div>
                              <div className='text-xs text-gray-500'>
                                {item.type}
                              </div>
                            </div>
                            <div className='text-right'>
                              <div>
                                {formatCurrency(item.amount)} × {item.quantity}
                              </div>
                              <div className='font-medium'>
                                {formatCurrency(item.total)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('discountAdjustment', {
        id: 'Discount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Discount'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.getValue() > 0 ? (
              <span className='text-red-600'>
                -{formatCurrency(info.getValue())}
              </span>
            ) : (
              <span className='text-gray-400'>-</span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('finalAmount', {
        id: 'Final Amount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Final Amount'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>{formatCurrency(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('paymentMethod', {
        id: 'Payment Method',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Method'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            <Badge variant='outline' className='capitalize'>
              {info.getValue()}
            </Badge>
          </div>
        ),
      }),
    ],
    [formatCurrency],
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  return (
    <TooltipProvider>
      <EnrollioTable
        ariaLabel='Upcoming payments'
        totalLabel='Upcoming payments'
        filterHeader={(table) => (
          <EnrollioTableSearch
            placeholder='Search parents...'
            value={
              (table.getColumn('Parent')?.getFilterValue() as
                | string
                | undefined) ?? ''
            }
            onChange={(value) => {
              table.getColumn('Parent')?.setFilterValue(value);
            }}
          />
        )}
        toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
        className='min-w-0'
        columns={columns}
        data={payments}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        getRowId={(row) => row.invoiceId}
        isUpdating={isUpdating}
        manualPagination
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        rowCount={rowCount}
        sorting={sorting}
      />
    </TooltipProvider>
  );
}
